import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import axios from 'axios';
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_KEY = process.env.REACT_APP_BACKEND_API_KEY;

// =====================================================================
// Code-Normalisierung gegen führende Nullen, Groß-/Kleinschreibung und
// Leerzeichen. WICHTIG, weil Scanner und DB den Code unterschiedlich
// formatieren können. Wird für den ArtNr-Abgleich UND die Sortierung genutzt.
// =====================================================================
const normalizeCode = (s) =>
  String(s ?? '').trim().toUpperCase().replace(/\s+/g, '');

// =====================================================================
// Sortierung nach Lagerort (aufsteigend) -> kurze Laufwege im Lager.
// "numeric: true" sorgt für natürliche Sortierung, d.h. A2 kommt vor
// A10 (bei reinem String-Vergleich wäre es umgekehrt).
// Positionen ohne Lagerort werden ans Ende gestellt.
// =====================================================================
const sortiereNachLagerort = (liste) =>
  [...liste].sort((a, b) => {
    const la = normalizeCode(a.lagerort);
    const lb = normalizeCode(b.lagerort);

    if (la === '' && lb === '') return 0;
    if (la === '') return 1;   // leere Lagerorte nach hinten
    if (lb === '') return -1;

    return la.localeCompare(lb, 'de', { numeric: true, sensitivity: 'base' });
  });

// =====================================================================
// Wiederverwendbare Scanner-Komponente (Kamera, QR-Codes)
// Öffnet sich als Vollbild-Overlay, gibt den gescannten String zurück
// und gibt die Kamera danach garantiert wieder frei.
// =====================================================================

// Typische getUserMedia-Fehler in verständlichen deutschen Text übersetzen
const kameraFehlerText = (err) => {
  const name = err?.name || '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Kamerazugriff wurde blockiert. Bitte in den Browser-Einstellungen die Kamera-Freigabe für diese Seite erlauben.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'Es wurde keine Kamera gefunden.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'Die Kamera wird gerade von einer anderen App verwendet. Bitte andere Kamera-Apps schließen und erneut versuchen.';
    default:
      return 'Kamera konnte nicht gestartet werden. Bitte erneut versuchen.';
  }
};

const ArtNrScanner = ({ erwarteteArtNr, onErfolg, onTrotzdem, onClose }) => {
  const hasScanned = useRef(false);
  const scannerRef = useRef(null);
  const abgebrochen = useRef(false); // true, sobald der Nutzer schließt/abbricht

  // NEU: Fehlermeldung als State -> wird als echtes, wegklickbares Pop-up angezeigt
  const [fehler, setFehler] = useState(null);
  // NEU: Zähler für "Erneut versuchen" -> erhöht sich, um den Start-Effekt neu auszulösen
  const [versuch, setVersuch] = useState(0);

  // Callbacks in Refs halten, damit der Start-Effekt NICHT bei jedem
  // Eltern-Render neu läuft (sonst wird die Kamera ständig neu gestartet).
  const onErfolgRef = useRef(onErfolg);
  useEffect(() => { onErfolgRef.current = onErfolg; }, [onErfolg]);

  useEffect(() => {
    // Refs bei jedem (Neu-)Start zurücksetzen
    abgebrochen.current = false;
    hasScanned.current = false;

    const scanner = new Html5Qrcode('reader', {
      // Nur QR-Codes -> schneller und treffsicherer.
      formatsToSupport: [
        Html5QrcodeSupportedFormats.QR_CODE,
      ],
    });
    scannerRef.current = scanner;

    // Sicheres Stoppen: nur stoppen, wenn wirklich noch gescannt wird.
    // Verhindert doppeltes stop() und den removeChild-Absturz (White-Screen).
    const sicherStoppen = async () => {
      try {
        if (scanner.getState() === Html5QrcodeScannerState.SCANNING) {
          await scanner.stop();
        }
        scanner.clear();
      } catch (e) {
        // Kamera war bereits gestoppt/entfernt -> ignorieren
      }
    };

    scanner
      .start(
        { facingMode: 'environment' }, // Rückkamera des Tablets
        { fps: 10, qrbox: { width: 250, height: 250 } }, // quadratische Box passt zu QR
        async (decodedText) => {
          if (hasScanned.current) return; // nur einmal auslösen
          hasScanned.current = true;
          await sicherStoppen();          // erst Kamera sauber schließen,
          onErfolgRef.current(decodedText); // dann Ergebnis melden
        },
        () => {} // Fehlversuche pro Frame ignorieren
      )
      .then(() => {
        // Falls der Nutzer schon abgebrochen hat, WÄHREND die Kamera startete:
        // Stream sofort wieder freigeben, damit die Kamera nicht im Hintergrund anbleibt.
        if (abgebrochen.current) sicherStoppen();
      })
      .catch((err) => {
        console.error('Kamera-Start fehlgeschlagen:', err);
        // NICHT automatisch schließen -> Fehler als echtes Pop-up im Overlay
        // anzeigen, das der Nutzer aktiv per Button wegklicken kann.
        if (!abgebrochen.current) setFehler(kameraFehlerText(err));
      });

    return () => {
      // Aufräumen beim Schließen/Neustart -> Kamera in jedem Fall freigeben
      abgebrochen.current = true;
      sicherStoppen();
    };
    // Läuft erneut, wenn "versuch" erhöht wird (Button "Erneut versuchen").
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versuch]);

  // NEU: Kamera neu starten -> Fehler zurücksetzen (damit #reader wieder
  // gerendert wird) und den Effect über den Zähler erneut auslösen.
  const erneutVersuchen = () => {
    setFehler(null);
    setVersuch((v) => v + 1);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.85)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
      }}
    >
      {fehler ? (
        /* === Echtes Fehler-Pop-up mit Handlungsoptionen === */
        <div
          className="card shadow p-4 text-center"
          style={{ maxWidth: 380, borderRadius: '12px' }}
        >
          <i className="bi bi-camera-video-off-fill text-danger" style={{ fontSize: '38px' }}></i>
          <h5 className="mt-3">Scannen nicht möglich</h5>
          <p className="text-muted mb-4">{fehler}</p>

          <div className="d-grid gap-2">
            <Button variant="primary" onClick={erneutVersuchen}>
              <i className="bi bi-arrow-clockwise me-2"></i>Erneut versuchen
            </Button>
            <Button variant="warning" onClick={onTrotzdem}>
              <i className="bi bi-unlock-fill me-2"></i>Trotzdem freischalten
            </Button>
            <Button variant="link" className="text-secondary" onClick={onClose}>
              Abbrechen
            </Button>
          </div>
        </div>
      ) : (
        /* === Normaler Scan-Modus === */
        <>
          <div style={{ color: '#fff', marginBottom: 12, textAlign: 'center' }}>
            ArtNr <strong>{erwarteteArtNr}</strong> scannen
          </div>
          <div id="reader" style={{ width: '100%', maxWidth: 320 }} />
          <Button variant="light" className="mt-3" onClick={onClose}>
            Abbrechen
          </Button>
        </>
      )}
    </div>
  );
};

const MaterialanforderungPosition = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();

  // Daten aus dem Navigation-State ziehen
  const vorgangsId = state?.vorgangsId ?? '';
  const kdiNr = state?.kdiNr ?? '';
  const monteur = state?.monteur ?? '';
  const strasse = state?.strasse ?? '';
  const vorgangszeitpunkt = state?.vorgangszeitpunkt ?? '';

  const bearbeiten = state?.bearbeiten ?? false;

  // === States für die Mitarbeiter-Auswahl (Richter) ===
  const [mitarbeiterListe, setMitarbeiterListe] = useState([]);
  const [richterId, setRichterId] = useState(state?.richter ?? '');

  // === NEU: Welche Zeile scannt gerade ihren Lagerort? (null = keine) ===
  const [scanIndex, setScanIndex] = useState(null);

  // Die raw-Liste holen, filtern, nach Lagerort sortieren und zwischenspeichern
  const artikelListe = useMemo(() => {
    const raw = state?.artikelListe ?? [];

    // Im Nur-Lese-Modus wird nicht gefiltert, aber trotzdem sortiert
    if (!bearbeiten) {
      return sortiereNachLagerort(raw);
    }
    const bekannteFaelle = raw
      .map(art => art.fall ? String(art.fall).trim().toLowerCase() : '')
      .filter(fallText => fallText !== '');

    const gefiltert = raw.filter(art => {
      const isIntern = art.interneBestellung === true || art.interneBestellung === 1;
      const artNrClean = String(art.artNr).trim().toLowerCase();
      const isFallArtikel = bekannteFaelle.includes(artNrClean);
      return isIntern && !isFallArtikel;
    });

    // NEU: aufsteigend nach Lagerort sortieren -> kurze Laufwege im Lager
    return sortiereNachLagerort(gefiltert);
  }, [state?.artikelListe, bearbeiten]);

  const [daten, setdaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setusername] = useState('');

  // === Name des ausgewählten Richters für die Text-Anzeige ermitteln ===
  const richterNameAnzeige = useMemo(() => {
    if (!richterId || mitarbeiterListe.length === 0) return '-';
    const gefunden = mitarbeiterListe.find(m => String(m.ID) === String(richterId));
    return gefunden ? `${gefunden.Vorname} ${gefunden.Nachname}` : '-';
  }, [richterId, mitarbeiterListe]);

  // === Initialisierung beim Laden ===
  useEffect(() => {
    if (localStorage.getItem("loginData") == null) {
      navigate("/dhworld/login");
      return;
    }

    const d = JSON.parse(localStorage.getItem("loginData"));
    setusername(d.username);

    const initialisiert = artikelListe.map(art => {
      let initialLagerMenge = '';
      if (art.neuerLagerbestand !== null &&
          art.neuerLagerbestand !== undefined &&
          String(art.neuerLagerbestand) !== 'null') {
        initialLagerMenge = String(art.neuerLagerbestand);
      }

      let initialPickMenge = '';
      if (art.pickMenge !== null &&
          art.pickMenge !== undefined &&
          String(art.pickMenge) !== 'null') {
        initialPickMenge = String(art.pickMenge);
      }

      const bereitsBearbeitet = art.pickMenge !== null;

      return {
        ...art,
        pickMengeStr: initialPickMenge,
        lagerMengeStr: initialLagerMenge,
        status: bereitsBearbeitet ? 'geschlossen' : 'offen',
        error: bereitsBearbeitet ? false : true,
        // NEU: bereits bearbeitete Zeilen gelten als geprüft, sonst muss gescannt werden
        artNrGeprueft: bereitsBearbeitet,
      };
    });

    setdaten(initialisiert);

    // === Mitarbeiter-Daten aus dem Backend abrufen ===
    const fetchMitarbeiter = async () => {
      try {
        const headers = {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY
        };
        const response = await axios.get(`${BACKEND_BASE_URL}/matmitarbeiter`, { headers });
        setMitarbeiterListe(response.data);
      } catch (err) {
        console.error("Fehler beim Laden der Mitarbeiter:", err);
        toast.error("Mitarbeiterliste konnte nicht geladen werden!");
      } finally {
        setLoading(false);
      }
    };

    fetchMitarbeiter();
  }, [artikelListe, navigate]);

  // === NEU: Ergebnis eines ArtNr-Scans verarbeiten ===
  const handleScan = useCallback((index, scannedValue) => {
    setScanIndex(null); // Scanner-Overlay schließen
    setdaten(prev => {
      const row = prev[index];
      if (!row) return prev;

      if (normalizeCode(scannedValue) === normalizeCode(row.artNr)) {
        toast.success(`ArtNr ${row.artNr} bestätigt.`);
        const updated = [...prev];
        updated[index] = { ...row, artNrGeprueft: true };
        return updated;
      }

      toast.error(
        `Falsche ArtNr! Erwartet: ${row.artNr || '-'}, gescannt: ${scannedValue}`
      );
      return prev; // nichts freigeben
    });
  }, []);

  // === NEU: Position ohne erfolgreichen Scan manuell freischalten ===
  // Wird aus der Fehlerkarte ("Trotzdem freischalten") aufgerufen, falls die
  // Kamera nicht funktioniert. Setzt artNrGeprueft = true, damit gearbeitet
  // werden kann.
  const handleTrotzdemFreischalten = useCallback((index) => {
    setScanIndex(null); // Scanner-Overlay schließen
    setdaten(prev => {
      const row = prev[index];
      if (!row) return prev;
      const updated = [...prev];
      updated[index] = { ...row, artNrGeprueft: true };
      return updated;
    });
    toast.info('Position ohne Scan freigeschaltet.');
  }, []);

  // === Logik für Sonderfall-Erkennung ===
  const allowNegativeForPick = (item) => {
    const r = parseFloat(String(item.sollMenge).replace(',', '.'));
    return isNaN(r) || r === -1;
  };

  // === Eingabevalidierung (Zahlen-Parser) ===
  const validateNumber = (value, allowNegative = false) => {
    if (value === '') return '';
    const cleaned = value.replace(',', '.');
    const regex = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (!regex.test(cleaned)) return '';
    if (cleaned === '-' || cleaned === '.') return '';
    return cleaned;
  };

  // === Wert aktualisieren bei Tastatur-Eingabe ===
  const handleInputChange = (index, field, value) => {
    if (!bearbeiten) return;

    const updated = [...daten];
    const row = updated[index];

    const allowNeg = field === 'pickMengeStr' && allowNegativeForPick(row);
    const valid = validateNumber(value, allowNeg);

    row[field] = valid;
    setdaten(updated);
  };

  // === + / - Klick-Buttons ===
  const adjustValue = (index, field, delta) => {
    if (!bearbeiten) return;

    const updated = [...daten];
    const row = updated[index];

    let current = parseFloat(row[field] || 0);
    if (isNaN(current)) current = 0;

    const negAllowedForPick = field === 'pickMengeStr' && allowNegativeForPick(row);
    let next = current + delta;

    if (negAllowedForPick) {
      if (next < -1) next = -1;
    } else {
      if (next < 0) next = 0;
    }

    row[field] = String(next);
    setdaten(updated);
  };

  // === Einzelne Zeile abschließen (Schloss) ===
  const toggleStatus = (index) => {
    if (!bearbeiten) return;

    const updated = [...daten];
    const item = updated[index];

    // NEU: Ohne geprüfte ArtNr darf nicht abgeschlossen werden
    if (!item.artNrGeprueft) {
      toast.warning(`Position ${item.artNr}: Bitte zuerst die ArtNr scannen!`);
      return;
    }

    if (item.status === 'geschlossen') {
      item.status = 'offen';
      item.error = true;
      setdaten(updated);
      return;
    }

    if (item.pickMengeStr === '') {
      toast.warning(`Position ${item.artNr}: Pick-Menge darf nicht leer sein! Bitte Wert (z.B. 0) eintragen.`);
      return;
    }

    const pick = parseFloat(String(item.pickMengeStr).replace(',', '.'));
    const soll = parseFloat(String(item.sollMenge).replace(',', '.'));
    const negAllowed = allowNegativeForPick(item);

    if (negAllowed) {
      if (pick !== -1) {
        toast.warning(`Position ${item.artNr}: Im Sonderfall muss die Pick-Menge genau -1 sein.`);
        return;
      }
    } else {
      const s = isNaN(soll) ? 0 : soll;
      if (!(pick === 0 || pick >= s)) {
        toast.warning(`Position ${item.artNr}: Pick-Menge muss 0 oder >= der Soll-Menge (${s}) sein!`);
        return;
      }
    }

    item.status = 'geschlossen';
    item.error = false;
    setdaten(updated);
  };

  // === Daten an das NodeJS-Backend & PHP-Excel-Skript absenden ===
  const handleSubmit = async () => {
    // 1. Pflichtfeld-Validierung für den Richter
    if (!richterId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter (Richter) aus, bevor Sie den Auftrag abschließen!");
      return;
    }

    // 2. Validierung der offenen Positionen
    const offenePositionen = daten.filter(d => d.status !== 'geschlossen');
    if (offenePositionen.length > 0) {
      const updated = daten.map(d => ({
        ...d,
        error: d.status !== 'geschlossen',
      }));
      setdaten(updated);
      toast.error("Es gibt noch offene Positionen, bitte zuerst alle Zeilen mit dem Schloss sperren!");
      return;
    }

    try {
      setLoading(true);
      const headersNode = {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      };

      // SCHRITT A: Updates im NodeJS-Backend speichern
      for (const art of daten) {
        const payloadArtikel = {
          pickMenge: parseFloat(art.pickMengeStr) || 0,
          neuerLagerbestand: art.lagerMengeStr === '' ? null : parseFloat(art.lagerMengeStr)
        };
        await axios.put(`${BACKEND_BASE_URL}/artikel/${art.id}`, payloadArtikel, { headers: headersNode });
      }

      const payloadAnforderung = {
        lagerAbgeschlossen: true,
        vorgangAbgeschlossen: false,
        richter: parseInt(richterId, 10)
      };
      await axios.put(`${BACKEND_BASE_URL}/materialanforderungen/${id}`, payloadAnforderung, { headers: headersNode });

      console.log("NodeJS-Backend erfolgreich aktualisiert.");

      // SCHRITT B: Excel-Datei über PHP erzeugen
      const payloadExcel = {
        dokumentNummer: vorgangsId,
        vorgangsdatum: vorgangszeitpunkt,
        intnummer: id,
        kommission: kdiNr,
        sachbearbeiter: richterNameAnzeige,
        strasse: strasse,
        username: monteur,
        positionen: daten.map(d => ({
          artikelnummer: d.artNr,
          bezeichnung: d.kurztext,
          richtMenge: d.sollMenge,
          pickMenge: parseFloat(d.pickMengeStr) || 0,
          lagerMenge: d.lagerMengeStr === '' ? null : parseFloat(d.lagerMengeStr),
          artReferenz: d.lagerort || '',
          status: d.status,
        })),
      };

      const phpRes = await axios.post(
        'https://dhworld.dietenmeier-harsch.de/create-materialanforderung.php',
        payloadExcel,
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
      );

      console.log("Response create.php:", phpRes.data);
      toast.success(`Materialanforderung ${vorgangsId} abgeschlossen und Excel-Datei erfolgreich erzeugt!`);

      setTimeout(() => {
        navigate(-1);
      }, 1500);

    } catch (err) {
      console.error("Fehler im Gesamtprozess:", err);
      if (err.config?.url?.includes('create.php')) {
        toast.error("Lagerdaten gespeichert, aber Fehler beim Erzeugen der Excel-Datei!");
      } else {
        toast.error("Verbindungsfehler: Daten konnten nicht im Backend gespeichert werden!");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center" style={{ paddingTop: "150px" }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#eceff2', minHeight: '100vh' }} className="container-fluid">
      <ToastContainer position="top-center" autoClose={2500} />

      {/* === NEU: Scanner-Overlay, nur wenn eine Zeile scannt === */}
      {scanIndex !== null && daten[scanIndex] && (
        <ArtNrScanner
          erwarteteArtNr={daten[scanIndex].artNr}
          onErfolg={(code) => handleScan(scanIndex, code)}
          onTrotzdem={() => handleTrotzdemFreischalten(scanIndex)}
          onClose={() => setScanIndex(null)}
        />
      )}

      <div className="row pt-4">
        <div className='col-12'>
          <Link to={-1}>
            <i style={{ fontSize: '25px', color: '#333' }} className="bi bi-arrow-return-left"></i>
          </Link>
          <br /><br />
          <h1>
            Positionen zur Materialanforderung {vorgangsId}
            {!bearbeiten && <span className="badge bg-secondary ms-3" style={{ fontSize: '1rem' }}>Nur-Lese-Modus</span>}
          </h1>
          <h5>Monteur: {monteur} | KDI: {kdiNr} | Straße: {strasse} | Richter: {richterNameAnzeige} | Bearbeiter: {username} | Eingangsdatum: {vorgangszeitpunkt ? new Date(vorgangszeitpunkt).toLocaleString('de-DE') : '-'}</h5>
        </div>
      </div>

      <br /><br />

      <Row className="g-4">
        {daten && daten.length > 0 ? (
          <div className="table-responsive-sm">
            <Table bordered hover bg="white">
              <thead className="table-dark">
                <tr>
                  <th>ArtNr</th>
                  <th>Kurztext</th>
                  <th>Lagerort</th>
                  <th>Soll-Menge</th>
                  <th>Bestand (Alt)</th>
                  <th>Typ</th>
                  <th style={{ width: '180px' }}>Pick-Menge</th>
                  <th style={{ width: '220px' }}>Neuer Bestand</th>
                  <th className="text-center">ArtNr / Status</th>
                </tr>
              </thead>
              <tbody>
                {daten.map((v, index) => {
                  let bgColor = '#fff';
                  if (bearbeiten && v.error) {
                    bgColor = '#ffcccc';
                  } else if (v.status === 'geschlossen') {
                    bgColor = '#c8f7c5';
                  }

                  const currentPick = parseFloat(v.pickMengeStr) || 0;
                  const expectedStock = v.lagerbestandBeiAbfrage - currentPick;

                  // NEU: Eingaben erst nach geprüfter ArtNr freigeben
                  const eingabeGesperrt = !bearbeiten || v.status === 'geschlossen' || !v.artNrGeprueft;

                  return (
                    <tr key={index} style={{ backgroundColor: bgColor, transition: 'background-color 0.3s' }}>
                      <td>
                        {v.artNr}
                        {bearbeiten && v.artNrGeprueft && (
                          <i className="bi bi-check-circle-fill text-success ms-1" title="ArtNr gescannt & bestätigt"></i>
                        )}
                      </td>
                      <td>{v.kurztext}</td>
                      <td>{v.lagerort}</td>
                      <td><strong>{v.sollMenge}</strong></td>
                      <td>{v.lagerbestandBeiAbfrage}</td>

                      <td>
                        {v.interneBestellung ? (
                          <span className="badge bg-warning text-dark">Intern</span>
                        ) : (
                          <span className="badge bg-light text-secondary border">Normal</span>
                        )}
                        {v.fall && (
                          <span className={`badge ms-1 ${v.fall === 'Overnight' ? 'bg-danger' : 'bg-primary'}`}>
                            {v.fall}
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-1 flex-nowrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={eingabeGesperrt}
                            onClick={() => adjustValue(index, 'pickMengeStr', -1)}
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                          <input
                            type="text"
                            className="form-control text-center"
                            style={{ minWidth: '70px', fontSize: '0.9rem' }}
                            value={v.pickMengeStr}
                            disabled={eingabeGesperrt}
                            onChange={(e) => handleInputChange(index, 'pickMengeStr', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={eingabeGesperrt}
                            onClick={() => adjustValue(index, 'pickMengeStr', 1)}
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-1 flex-nowrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={eingabeGesperrt}
                            onClick={() => adjustValue(index, 'lagerMengeStr', -1)}
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                          <input
                            type="text"
                            className="form-control text-center"
                            style={{ minWidth: '70px', fontSize: '0.9rem' }}
                            placeholder={String(expectedStock)}
                            value={v.lagerMengeStr}
                            disabled={eingabeGesperrt}
                            onChange={(e) => handleInputChange(index, 'lagerMengeStr', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={eingabeGesperrt}
                            onClick={() => adjustValue(index, 'lagerMengeStr', 1)}
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>

                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ minWidth: '32px', marginLeft: '2px' }}
                            disabled={eingabeGesperrt}
                            onClick={() => handleInputChange(index, 'lagerMengeStr', '')}
                            title="Feld leeren"
                          >
                            <i className="bi bi-eraser-fill"></i>
                          </Button>
                        </div>
                      </td>

                      <td className="text-center">
                        {/* NEU: Vor dem Pick muss die ArtNr gescannt werden */}
                        {bearbeiten && !v.artNrGeprueft ? (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setScanIndex(index)}
                          >
                            <i className="bi bi-upc-scan me-1"></i> ArtNr scannen
                          </Button>
                        ) : (
                          <i
                            className={`bi ${v.status === 'geschlossen' ? 'bi-lock-fill text-success' : 'bi-unlock text-secondary'}`}
                            style={{
                              fontSize: '22px',
                              cursor: bearbeiten ? 'pointer' : 'not-allowed',
                              opacity: bearbeiten ? 1 : 0.6
                            }}
                            onClick={() => toggleStatus(index)}
                          ></i>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <Col xs={12}>
            <div className="p-4 text-center bg-white rounded">
              Keine {bearbeiten ? 'internen' : ''} Artikel-Positionen gefunden.
            </div>
          </Col>
        )}
      </Row>

      {/* === Dropdown-Menü für Richter-Auswahl === */}
      {bearbeiten && (
        <Row className="justify-content-center mt-5">
          <Col xs={12} md={6} lg={4}>
            <div className="card shadow-sm border-0 p-3" style={{ borderRadius: '10px' }}>
              <label htmlFor="richterSelect" className="form-label fw-bold text-dark">
                <i className="bi bi-person-badge me-2 text-success"></i>
                Mitarbeiter (Richter) auswählen <span className="text-danger">*</span>
              </label>
              <select
                id="richterSelect"
                className="form-select form-select-lg"
                style={{ fontSize: '16px', borderRadius: '8px', cursor: 'pointer' }}
                value={richterId}
                onChange={(e) => setRichterId(e.target.value)}
              >
                <option value="">-- Bitte wählen --</option>
                {mitarbeiterListe.map((m) => (
                  <option key={m.ID} value={m.ID}>
                    {m.Nachname}, {m.Vorname}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        </Row>
      )}

      {/* === Absenden-Button === */}
      {bearbeiten && (
        <div className="text-center my-4">
          <Button
            onClick={handleSubmit}
            variant="success"
            style={{
              padding: '12px 50px',
              borderRadius: '10px',
              fontWeight: '600',
              fontSize: '18px',
              boxShadow: '0 4px 12px rgba(40,167,69,0.3)',
            }}
          >
            📦 Kommissionierung abschließen &amp; senden
          </Button>
        </div>
      )}
    </div>
  );
};

export default MaterialanforderungPosition;
